import { PanelHeader, PanelHeaderButton } from '~/design-system/components/v1_5/Layout';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { cn } from '~/lib/utils';
import { VIEW_LABEL } from './KindMenu';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './DirectoryHeader.module.css';
import type { ArtifactLayout, DirectoryView, HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface DirectoryHeaderProps {
  kind: DirectoryView;
  /**
   * The bare numeral after the caret. Omit it while the list is still loading — a count of nothing
   * is not the same statement as a count of zero, and the skeleton below already says "wait".
   */
  count?: number;
  kindMenuOpen?: boolean;
  onKindClick?: () => void;
  addMenuOpen?: boolean;
  onAddClick?: () => void;
  /**
   * Draw no `+` at all.
   *
   * Same shape as `HeaderV15`'s `hideAdd`, and for the same reason: the host says so out loud
   * rather than the band inferring it from a missing handler. It exists for the one view that has
   * nothing to add TO — `Other sources` lists what connected apps hold, and every row the add menu
   * makes lands in the deck, so a `+` there would announce "Add to Other sources" over a menu that
   * cannot put anything in Other sources.
   */
  hideAdd?: boolean;
  /**
   * Artifacts only: how the list under the band draws them. Omit `onLayoutClick` and no toggle is
   * drawn — the other kinds have one layout each.
   */
  layout?: ArtifactLayout;
  onLayoutClick?: () => void;
  theme?: HeaderTheme;
  /** Stories and visual tests only: `'kind'`, `'layout'` or `'add'`. */
  forceHover?: string | null;
  className?: string;
}

/**
 * The panel's 36px band: the kind, its caret, its count, and one `+`.
 *
 * This is the dedicated page's header, not the layout boards' 30px "6 items" strip (D8) — a real
 * 36px band with a bottom hairline, matching every other panel header in the shell so the three
 * panes line up along one horizon.
 *
 * It is `PanelHeader` with the padding pulled in 6px on the left, and that shift is what lets the
 * kind switcher be a chip: the chip's own 6px of padding puts the word "Artifacts" back on the 12px
 * the design draws it at, while the chip itself is inset from the pane edge by exactly the 6px the
 * `+` gets on the other end. Without the shift either the word sits at 18 or the chip runs into the
 * seam.
 */
export function DirectoryHeader({
  kind,
  count,
  kindMenuOpen = false,
  onKindClick,
  addMenuOpen = false,
  onAddClick,
  hideAdd = false,
  layout = 'grid',
  onLayoutClick,
  theme: themeProp,
  forceHover = null,
  className,
}: DirectoryHeaderProps) {
  const theme = useV15Theme(themeProp);
  return (
    <PanelHeader
      theme={theme}
      className={cn(styles.band, className)}
      trailing={
        <>
          {onLayoutClick ? (
            <PanelHeaderButton
              // A command, not a state (the static-arrows ruling): the glyph is the view you GET by
              // pressing, so the rail offers the list and the list offers the grid. No `-fill` twin
              // and no `active` — the layout on screen is its own indicator.
              icon={layout === 'list' ? 'grid' : 'bullet-list'}
              label={layout === 'list' ? 'Show artifacts as a rail' : 'Show artifacts as a list'}
              theme={theme}
              forceHover={forceHover === 'layout'}
              onClick={onLayoutClick}
            />
          ) : null}
          {hideAdd ? null : (
            <PanelHeaderButton
              icon="add"
              // Open paints no chip: it swaps to the `-fill` twin at the same tone, which is how
              // every active control in v1.5 says it is live. Paper draws a light-10 pill here
              // instead; the shipped rule wins, because a chip that persists reads as a second
              // element on the band.
              activeIcon="add-fill"
              active={addMenuOpen}
              // 16, not the band's usual 15. A plus is four thin strokes and a hair of extra size
              // is what keeps it weighing the same as the three-line glyphs on the other panel
              // headers.
              glyphSize={16}
              label={`Add to ${VIEW_LABEL[kind]}`}
              // It opens a menu, so it announces one — the same pair the kind switcher three pixels
              // to its left already sets. `IconSlot` drops its `aria-pressed` once these are here:
              // "not pressed" is not what a closed menu is.
              ariaHasPopup="menu"
              ariaExpanded={addMenuOpen}
              theme={theme}
              forceHover={forceHover === 'add'}
              onClick={onAddClick}
            />
          )}
        </>
      }
    >
      <button
        type="button"
        className={cn(
          themes[theme],
          styles.switcher,
          forceHover === 'kind' && styles.forceHover,
        )}
        aria-haspopup="menu"
        aria-expanded={kindMenuOpen}
        onClick={onKindClick}
      >
        <span className={styles.kind}>{VIEW_LABEL[kind]}</span>
        {/* The caret flips rather than rotating, exactly as the file does it — a 14px chevron
            rotating 180deg passes through a horizontal bar on the way, and at this size that frame
            reads as a glitch rather than as motion. */}
        <span className={styles.caret}>
          <DeckIcon name={kindMenuOpen ? 'arrow-up-s' : 'arrow-down-s'} size={14} />
        </span>
        {/* Inside the button, not beside it. The count belongs to the kind — it is the count OF
            this kind, and the folded rail draws it as the kind's own attribute — so it goes in the
            same target rather than sitting next to it as an unclickable orphan, which also makes a
            14px caret a 60px thing to hit. `null` while loading, not 0: nothing has been counted. */}
        {count === undefined ? null : <span className={styles.count}>{count}</span>}
      </button>
    </PanelHeader>
  );
}
